import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertCircle, CheckCircle2, HardDrive, Minus, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { formatTimestamp } from "@/utils/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/platform/production-health`;

const BANNER_TEXT =
  "This page checks the currently running JFM environment. It does not identify or verify the deployed source-code version, and it cannot detect when the entire service is unavailable.";

const STATUS_TONE = {
  healthy: { color: "#166534", icon: CheckCircle2, label: "Healthy" },
  degraded: { color: "#b45309", icon: AlertCircle, label: "Degraded" },
  stale: { color: "#b45309", icon: AlertCircle, label: "Stale" },
  warning: { color: "#b45309", icon: AlertCircle, label: "Warning" },
  critical: { color: "#b91c1c", icon: AlertCircle, label: "Critical" },
  unavailable: { color: "#b45309", icon: AlertCircle, label: "Unavailable" },
  not_configured: { color: "#4b5563", icon: Minus, label: "Not configured" },
};

const PROBE_LABELS = {
  database: "Database",
  scheduler: "Scheduler",
  r2: "R2 storage",
  errors: "Error telemetry",
};

const apiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
};

function SchedulerJobs({ jobs }) {
  if (!jobs || jobs.length === 0) return null;
  return (
    <ul
      data-testid="platform-production-health-tile-scheduler-jobs"
      style={{
        margin: "0.5rem 0 0",
        padding: 0,
        listStyle: "none",
        display: "grid",
        gap: "0.25rem",
        fontSize: "0.78rem",
        color: "#374151",
      }}
    >
      {jobs.map((job) => {
        const tone = STATUS_TONE[job.status] || STATUS_TONE.unavailable;
        return (
          <li
            data-testid={`platform-production-health-tile-scheduler-job-${job.job_name}`}
            key={job.job_name}
            style={{ display: "flex", gap: "0.5rem", alignItems: "baseline" }}
          >
            <span style={{ fontWeight: 600 }}>{job.job_name}</span>
            <span
              data-testid={`platform-production-health-tile-scheduler-job-${job.job_name}-status`}
              style={{ color: tone.color, fontWeight: 600 }}
            >
              {tone.label}
            </span>
            <span style={{ color: "#6b7280" }}>
              {job.last_run_at ? `last run ${formatTimestamp(job.last_run_at)}` : "never run"}
            </span>
            {job.last_error_code && (
              <span style={{ color: "#b45309" }}>· {job.last_error_code}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ErrorsSummary({ probe, onOpenDetails }) {
  const total = probe?.total ?? 0;
  const critical = probe?.critical ?? 0;
  const windowHours = probe?.window_hours ?? 24;
  const groups = probe?.top_groups || [];
  return (
    <div
      data-testid="platform-production-health-tile-errors-summary"
      style={{ marginTop: "0.5rem", fontSize: "0.8rem", color: "#374151" }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.4rem", flexWrap: "wrap" }}>
        <span>
          <strong data-testid="platform-production-health-tile-errors-total">{total}</strong> error
          {total === 1 ? "" : "s"} in last {windowHours}h
        </span>
        {critical > 0 && (
          <span data-testid="platform-production-health-tile-errors-critical" style={{ color: "#b91c1c", fontWeight: 600 }}>
            · {critical} critical
          </span>
        )}
        <button
          data-testid="platform-production-health-tile-errors-view"
          onClick={onOpenDetails}
          style={{
            marginLeft: "auto", background: "transparent", border: "1px solid #e5e7eb",
            padding: "2px 8px", borderRadius: 4, cursor: "pointer", fontSize: "0.78rem",
          }}
          type="button"
        >
          View recent
        </button>
      </div>
      {groups.length > 0 && (
        <ul
          data-testid="platform-production-health-tile-errors-groups"
          style={{ margin: "0.45rem 0 0", padding: 0, listStyle: "none", display: "grid", gap: "0.2rem" }}
        >
          {groups.slice(0, 3).map((g) => (
            <li
              data-testid={`platform-production-health-tile-errors-group-${g.fingerprint}`}
              key={g.fingerprint}
              style={{ display: "flex", gap: "0.4rem", alignItems: "baseline" }}
            >
              <span style={{ fontWeight: 700, color: "#111827" }}>×{g.count}</span>
              <span style={{ color: "#6b7280" }}>{g.method}</span>
              <span style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", color: "#111827" }}>
                {g.route}
              </span>
              <span style={{ marginLeft: "auto", color: "#b45309" }}>{g.exception_type}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ErrorDetailsDrawer({ open, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setBusy(true); setErr("");
    axios.get(`${API}/errors?limit=100`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiErrorMessage(e, "Could not load error details.")))
      .finally(() => setBusy(false));
  }, [open]);
  if (!open) return null;
  return (
    <div
      data-testid="platform-production-health-errors-drawer"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)",
        display: "flex", justifyContent: "flex-end", zIndex: 60,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(720px, 100vw)", background: "#fff", height: "100%",
          padding: "1rem 1.25rem", overflowY: "auto", boxShadow: "-8px 0 24px rgba(15,23,42,0.15)",
        }}
      >
        <header style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <h3 style={{ margin: 0 }}>Recent errors</h3>
          <button
            data-testid="platform-production-health-errors-drawer-close"
            onClick={onClose}
            style={{ marginLeft: "auto", background: "transparent", border: "1px solid #e5e7eb", padding: "2px 10px", borderRadius: 4, cursor: "pointer" }}
            type="button"
          >Close</button>
        </header>
        {busy && <p>Loading…</p>}
        {err && <p role="alert" style={{ color: "#b91c1c" }}>{err}</p>}
        {data && (
          <>
            <p style={{ color: "#6b7280", fontSize: "0.8rem", margin: "0.4rem 0 0.8rem" }}>
              {data.summary.total} error{data.summary.total === 1 ? "" : "s"} in last {data.summary.window_hours}h ·
              Retention: {data.retention.max_events} events / {data.retention.ttl_days} days
            </p>
            <table data-testid="platform-production-health-errors-drawer-table" style={{ width: "100%", fontSize: "0.78rem", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", background: "#f9fafb" }}>
                  <th style={{ padding: 6 }}>When</th>
                  <th style={{ padding: 6 }}>Sev</th>
                  <th style={{ padding: 6 }}>Method</th>
                  <th style={{ padding: 6 }}>Route</th>
                  <th style={{ padding: 6 }}>Type</th>
                  <th style={{ padding: 6 }}>Message</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((ev, i) => (
                  <tr data-testid={`platform-production-health-errors-drawer-row-${i}`} key={`${ev.timestamp}-${i}`} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={{ padding: 6, whiteSpace: "nowrap" }}>{formatTimestamp(ev.timestamp)}</td>
                    <td style={{ padding: 6, color: ev.severity === "critical" ? "#b91c1c" : "#b45309", fontWeight: 600 }}>{ev.severity}</td>
                    <td style={{ padding: 6 }}>{ev.method}</td>
                    <td style={{ padding: 6, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>{ev.route}</td>
                    <td style={{ padding: 6 }}>{ev.exception_type}</td>
                    <td style={{ padding: 6, color: "#374151" }}>{ev.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

function ProbeTile({ probeKey, probe, onOpenErrorDetails }) {
  const tone = STATUS_TONE[probe.status] || STATUS_TONE.unavailable;
  const Icon = tone.icon;
  return (
    <article
      className="platform-owner-email-editor"
      data-testid={`platform-production-health-tile-${probeKey}`}
      style={{ padding: "0.75rem 0.9rem" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <Icon aria-hidden="true" size={16} color={tone.color} />
        <strong data-testid={`platform-production-health-tile-${probeKey}-label`}>
          {PROBE_LABELS[probeKey] || probeKey}
        </strong>
        <span
          data-testid={`platform-production-health-tile-${probeKey}-status`}
          style={{ marginLeft: "auto", color: tone.color, fontWeight: 600 }}
        >
          {tone.label}
        </span>
      </div>
      <dl style={{ margin: "0.5rem 0 0", fontSize: "0.8rem", color: "#374151" }}>
        <div>
          <dt style={{ display: "inline", fontWeight: 500 }}>Checked at: </dt>
          <dd
            data-testid={`platform-production-health-tile-${probeKey}-checked-at`}
            style={{ display: "inline", margin: 0 }}
          >
            {formatTimestamp(probe.checked_at)}
          </dd>
        </div>
        <div>
          <dt style={{ display: "inline", fontWeight: 500 }}>Duration: </dt>
          <dd
            data-testid={`platform-production-health-tile-${probeKey}-duration`}
            style={{ display: "inline", margin: 0 }}
          >
            {probe.duration_ms} ms
          </dd>
        </div>
        {probe.reason_code && (
          <div>
            <dt style={{ display: "inline", fontWeight: 500 }}>Reason: </dt>
            <dd
              data-testid={`platform-production-health-tile-${probeKey}-reason`}
              style={{ display: "inline", margin: 0 }}
            >
              {probe.reason_code}
            </dd>
          </div>
        )}
      </dl>
      {probeKey === "scheduler" && <SchedulerJobs jobs={probe.jobs} />}
      {probeKey === "errors" && <ErrorsSummary probe={probe} onOpenDetails={onOpenErrorDetails} />}
    </article>
  );
}

export default function RuntimeHealthPanel() {
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorsDrawerOpen, setErrorsDrawerOpen] = useState(false);

  const loadSnapshot = useCallback(async ({ notifyOnError = false } = {}) => {
    setIsLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/snapshot`);
      setSnapshot(response.data);
    } catch (err) {
      if (err?.response?.status === 429) {
        const retry = err.response.data?.retry_after_seconds || 60;
        setError(`Rate limit reached. Try again in ${retry} seconds.`);
      } else {
        const message = apiErrorMessage(err, "Runtime health snapshot could not be loaded.");
        setError(message);
        if (notifyOnError) toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);

  const scopeChip = snapshot?.scope === "production" ? "Production" : "Preview / sandbox";

  return (
    <div data-testid="platform-production-health-panel">
      <header
        className="platform-header"
        style={{ marginTop: "0.4rem" }}
      >
        <div>
          <span className="operations-kicker">Guardian</span>
          <h2 data-testid="platform-production-health-heading" style={{ margin: 0 }}>
            Runtime Health
          </h2>
          <p data-testid="platform-production-health-banner">{BANNER_TEXT}</p>
        </div>
        <div className="platform-account-actions">
          <span data-testid="platform-production-health-scope-chip">
            <HardDrive aria-hidden="true" size={14} /> Scope: {scopeChip}
          </span>
          <button
            data-testid="platform-production-health-refresh-button"
            disabled={isLoading}
            onClick={() => loadSnapshot({ notifyOnError: true })}
            type="button"
          >
            <RefreshCw size={15} />
            {isLoading ? "Checking…" : "Refresh"}
          </button>
        </div>
      </header>

      <section
        className="platform-access-boundary"
        data-testid="platform-production-health-boundary"
      >
        <div className="platform-access-boundary-heading">
          <ShieldAlert aria-hidden="true" size={22} />
          <div>
            <span className="operations-kicker">Read-only</span>
            <h3 style={{ margin: "0 0 0.4rem" }}>Guardian is read-only.</h3>
            <p>
              This surface never creates, updates, or deletes JFM records; never triggers
              scheduled jobs, purges, renewals, retries, or restarts; never modifies code,
              rules, configuration, or files; and never runs shell commands or arbitrary
              outbound HTTP requests.
            </p>
          </div>
        </div>
      </section>

      {error && (
        <section
          className="platform-dashboard-error"
          data-testid="platform-production-health-error"
          role="alert"
        >
          {error}
        </section>
      )}

      {snapshot && (
        <>
          <section
            className="platform-overview"
            data-testid="platform-production-health-tiles"
          >
            {["database", "scheduler", "r2", "errors"].map((key) => (
              <ProbeTile
                key={key}
                onOpenErrorDetails={() => setErrorsDrawerOpen(true)}
                probe={snapshot[key]}
                probeKey={key}
              />
            ))}
          </section>
          <ErrorDetailsDrawer open={errorsDrawerOpen} onClose={() => setErrorsDrawerOpen(false)} />

          <section
            className="platform-owner-email-editor"
            data-testid="platform-production-health-limitations"
          >
            <h3 style={{ marginTop: 0 }}>Limitations</h3>
            <ul>
              {snapshot.limitations.map((line, index) => (
                <li
                  data-testid={`platform-production-health-limitation-${index}`}
                  key={line}
                >
                  {line}
                </li>
              ))}
            </ul>
          </section>

          <section
            className="platform-owner-email-editor"
            data-testid="platform-production-health-summary"
          >
            <h3 style={{ marginTop: 0 }}>Snapshot summary</h3>
            <dl style={{ fontSize: "0.85rem", color: "#374151" }}>
              <div>
                <dt style={{ display: "inline", fontWeight: 500 }}>Snapshot id: </dt>
                <dd
                  data-testid="platform-production-health-summary-snapshot-id"
                  style={{ display: "inline", margin: 0 }}
                >
                  {snapshot.snapshot_summary.snapshot_id}
                </dd>
              </div>
              <div>
                <dt style={{ display: "inline", fontWeight: 500 }}>Generated at: </dt>
                <dd
                  data-testid="platform-production-health-summary-generated-at"
                  style={{ display: "inline", margin: 0 }}
                >
                  {formatTimestamp(snapshot.snapshot_summary.generated_at)}
                </dd>
              </div>
              <div>
                <dt style={{ display: "inline", fontWeight: 500 }}>Review type: </dt>
                <dd
                  data-testid="platform-production-health-summary-review-type"
                  style={{ display: "inline", margin: 0 }}
                >
                  {snapshot.snapshot_summary.review_type}
                </dd>
              </div>
              <div>
                <dt style={{ display: "inline", fontWeight: 500 }}>Unavailable: </dt>
                <dd
                  data-testid="platform-production-health-summary-unavailable"
                  style={{ display: "inline", margin: 0 }}
                >
                  {snapshot.snapshot_summary.unavailable.length
                    ? snapshot.snapshot_summary.unavailable.join(", ")
                    : "None"}
                </dd>
              </div>
            </dl>
          </section>
        </>
      )}
    </div>
  );
}
